// src/cockpit4/components/WebsiteGEODiagnosisPanel.tsx
// ============================================
// 官网 GEO 诊断（GEO+DSO 新工具 / 同步作为官网引流工具页）
//
// 结构（完全对应图1）：
//   ① Hero Banner：大标题「让官网更容易被 AI 理解与引用」
//        输入框 + 立即诊断（紫蓝渐变按钮）
//        下方：积分 / 生成时间 / 支持分享与下载PDF
//   ② 四大核心维度：技术可访问性 / 内容可回答性 / 品牌实体清晰度 / 证据与权威度
//   ③ CTA 条：爱搜提供官网建设 · 二维码联系客服 · 建设GEO官网按钮
//   ④ 诊断历史记录列表（空态）
// ============================================

import { useState } from "react";
import { motion } from "framer-motion";
import {
  Link2, ArrowRight, Sparkles, Clock, FileText, ShieldCheck, CheckCircle2,
  Globe, Sparkles as SparkIcon, Building2, Award, Zap, Download, Share2, Trash2, ExternalLink,
} from "lucide-react";
import EmptyState from "./_MonitorEmptyState";

const DEFAULT_DATE_RANGE = () => {
  const today = new Date();
  const d1 = new Date(today.getTime() - 6 * 24 * 3600 * 1000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { s: fmt(d1), e: fmt(today) };
};

interface HistoryItem {
  id: string;
  url: string;
  date: string;
  time: string;
  total: number;
  sub: { label: string; v: number; color: string }[];
  status: "完成" | "诊断中" | "失败";
}

export default function WebsiteGEODiagnosisPanel({
  onOpenMiner,
}: { onOpenMiner?: () => void }) {
  const [url, setUrl] = useState("");
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [, setDate] = useState(DEFAULT_DATE_RANGE());

  const run = () => {
    if (!url.trim()) return;
    setRunning(true);
    setTimeout(() => {
      const now = new Date();
      const h: HistoryItem = {
        id: Date.now().toString(),
        url: url.trim(),
        date: now.toISOString().slice(0,10),
        time: now.toTimeString().slice(0,5),
        total: Math.round(45 + Math.random()*50),
        sub: [
          { label: "技术可访问性", v: Math.round(55+Math.random()*40), color: "#8b5cf6" },
          { label: "内容可回答性", v: Math.round(55+Math.random()*40), color: "#8b5cf6" },
          { label: "品牌实体清晰度", v: Math.round(55+Math.random()*40), color: "#8b5cf6" },
          { label: "证据与权威度", v: Math.round(55+Math.random()*40), color: "#8b5cf6" },
        ],
        status: "完成",
      };
      setHistory([h, ...history]);
      setRunning(false);
    }, 900);
  };

  return (
    <div className="px-4 py-5 space-y-5">
      {/* ① Hero Banner */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl p-10 text-center relative overflow-hidden"
        style={{ background: "radial-gradient(1200px 400px at 50% -10%, rgba(139,92,246,0.22), rgba(34,211,238,0.10) 40%, rgba(255,255,255,0.02) 70%), linear-gradient(180deg, #0a0d18 0%, #0a0d18 100%)" }}>
        {/* 点缀 */}
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(rgba(139,92,246,0.5) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="relative">
          <h1 className="text-[36px] md:text-[44px] font-extrabold text-white leading-tight tracking-tight">
            让官网更容易被 <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">AI 理解与引用</span>
          </h1>
          <p className="mt-4 text-[15px] text-gray-400 max-w-3xl mx-auto">
            输入网站地址，快速诊断<span className="text-violet-300">技术可访问性</span>、
            <span className="text-cyan-300">内容可回答性</span>、
            <span className="text-fuchsia-300">品牌实体清晰度</span>与<span className="text-emerald-300">证据可信度</span>。
          </p>

          {/* URL 输入条 */}
          <div className="mt-8 max-w-3xl mx-auto">
            <div className="rounded-2xl p-[2px] bg-gradient-to-r from-violet-500/60 via-fuchsia-400/60 to-cyan-400/60 shadow-2xl shadow-violet-500/20">
              <div className="rounded-2xl bg-[#0d1120] px-4 py-2 flex items-center gap-2">
                <Link2 size={18} className="text-violet-400 shrink-0" />
                <input
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && run()}
                  placeholder="请输入网站地址，如 https://www.aidso.com"
                  className="flex-1 bg-transparent outline-none text-[15px] text-white placeholder:text-gray-500 py-2"
                />
                <button onClick={run} disabled={running || !url.trim()}
                  className="shrink-0 flex items-center gap-1.5 px-7 py-3 rounded-xl text-[15px] font-bold bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:opacity-95 disabled:opacity-60 shadow-lg shadow-violet-500/30 transition-all">
                  {running ? <Sparkles size={16} className="animate-spin" /> : <span>立即诊断</span>}
                  {!running && <ArrowRight size={16} />}
                </button>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-center gap-8 flex-wrap text-[13px] text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-4 h-4 rounded-full border border-violet-400/60 text-[10px] flex items-center justify-center text-violet-300">◎</span>
                每次消耗 <span className="text-violet-300 font-semibold mx-0.5">99</span> 积分（限时39）
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={13} className="text-violet-300" />
                预计生成时间 <span className="text-violet-300 font-semibold">5~10 分钟</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <FileText size={13} className="text-violet-300" />
                支持<span className="text-violet-300 font-semibold">分享与下载 PDF</span>
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ② 四大维度 */}
      <div>
        <div className="text-center mb-2">
          <div className="text-[12px] font-bold tracking-[0.25em] bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">GEO EVALUATION MODEL</div>
          <h2 className="mt-2 text-[28px] md:text-[32px] font-extrabold text-white tracking-tight">全方位 GEO 诊断四大核心维度</h2>
          <p className="mt-2 text-[13.5px] text-gray-500">100 分满分制评估体系，精准定位官网在 AI 搜索引擎中的引用短板</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <DimCard
            Icon={<Globe size={20} className="text-violet-400" />}
            iconBg="bg-violet-500/10"
            title="技术可访问性"
            en="TECHNICAL ACCESSIBILITY"
            desc="抓取、索引与页面解析"
          />
          <DimCard
            Icon={<SparkIcon size={20} className="text-violet-400" />}
            iconBg="bg-violet-500/10"
            title="内容可回答性"
            en="CONTENT ANSWERABILITY"
            desc="答案结构与问题覆盖"
          />
          <DimCard
            Icon={<Building2 size={20} className="text-violet-400" />}
            iconBg="bg-violet-500/10"
            title="品牌实体清晰度"
            en="BRAND ENTITY CLARITY"
            desc="品牌、产品与服务关系"
          />
          <DimCard
            Icon={<ShieldCheck size={20} className="text-emerald-400" />}
            iconBg="bg-emerald-500/10"
            title="证据与权威度"
            en="EVIDENCE & TRUSTWORTHINESS"
            desc="来源、作者与数据口径"
          />
        </div>
      </div>

      {/* ③ CTA 条：爱搜提供官网建设 + 二维码 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
        className="rounded-3xl p-8 md:p-10 relative overflow-hidden"
        style={{ background: "radial-gradient(800px 300px at 80% 20%, rgba(139,92,246,0.22), rgba(99,102,241,0.10) 40%, rgba(255,255,255,0.02) 75%)" }}>
        {/* 同心圆装饰 */}
        <div className="absolute right-20 top-10 w-[280px] h-[280px] rounded-full border border-violet-500/15" />
        <div className="absolute right-32 top-22 w-[190px] h-[190px] rounded-full border border-violet-500/20" />
        <div className="absolute right-44 top-34 w-[120px] h-[120px] rounded-full border border-violet-500/25" />

        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-[12.5px] font-bold tracking-wider flex items-center gap-1.5 text-violet-400">
              <span className="w-5 h-[3px] rounded bg-violet-400 inline-block" />
              爱搜提供官网建设
            </div>
            <h3 className="mt-3 text-[28px] md:text-[32px] font-extrabold text-white leading-tight tracking-tight">
              搭建 <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">GEO 品牌官网</span>，让 AI 更容易发现引用你
            </h3>
            <p className="mt-3 text-[14px] text-gray-400 max-w-2xl">
              从网站结构到内容运营，让品牌官网成为可持续积累的 AI 搜索资产。
            </p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              {[
                { c: "bg-violet-500/15 border border-violet-400/40 text-violet-200", t: "GEO 原生优化" },
                { c: "bg-violet-500/15 border border-violet-400/40 text-violet-200", t: "对话式持续运营" },
                { c: "bg-violet-500/15 border border-violet-400/40 text-violet-200", t: "持续增长" },
              ].map((p,i) => (
                <div key={i} className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-[12.5px] ${p.c}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400" /> {p.t}
                </div>
              ))}
            </div>
            <div className="mt-6">
              <button className="inline-flex items-center gap-1.5 px-6 py-3 rounded-xl text-[14px] font-bold bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:opacity-95 shadow-lg shadow-violet-500/25 transition-all">
                建设GEO官网 <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {/* 二维码卡 */}
          <div className="shrink-0 w-[240px] rounded-3xl bg-white/[0.04] border border-white/[0.08] backdrop-blur p-5 text-center">
            <div className="w-[180px] h-[180px] mx-auto rounded-2xl bg-white p-3 flex items-center justify-center relative">
              <QRCodeSVG />
              {/* 中心logo */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-md">
                <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="#6d28d9" d="M8 4h8l4 4v12a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4Z"/><path fill="#fff" d="M10 10a2 2 0 1 0 2-2 2 2 0 0 0-2 2Zm6 6a2 2 0 1 0 2-2 2 2 0 0 0-2 2Z"/></svg>
              </div>
            </div>
            <div className="mt-3 text-[14px] font-semibold text-white">扫码联系客服</div>
            <div className="mt-1 text-[12px] text-gray-500">获取 GEO 品牌官网建设方案</div>
          </div>
        </div>
      </motion.div>

      {/* ④ 诊断历史记录 */}
      <div>
        <div>
          <div className="text-[12.5px] font-bold tracking-[0.22em] bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">DIAGNOSIS RECORDS</div>
          <h2 className="mt-2 text-[26px] md:text-[30px] font-extrabold text-white tracking-tight">诊断历史记录</h2>
        </div>

        <div className="mt-6 rounded-2xl bg-white/[0.03] border border-white/[0.08] overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2 border-b border-white/[0.06]">
            <div className="flex items-center gap-2 text-[13px]">
              <Zap size={14} className="text-amber-400" />
              <span className="text-gray-400">共 <span className="text-white font-semibold">{history.length}</span> 条记录</span>
            </div>
            <button onClick={onOpenMiner}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] bg-white/[0.04] border border-white/[0.08] text-gray-400 hover:text-white hover:border-white/20">
              <Award size={13} /> 去 AI问题挖掘 推荐关键词
            </button>
          </div>
          {history.length === 0 ? (
            <EmptyState text="暂无诊断记录，输入官网地址立即诊断" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px] border-collapse">
                <thead>
                  <tr className="bg-white/[0.04] text-gray-400 text-[11.5px]">
                    <th className="text-left p-3 rounded-l-lg">网站地址</th>
                    <th className="text-left p-3">诊断时间</th>
                    <th className="text-left p-3">总分</th>
                    <th className="text-left p-3">四项评分</th>
                    <th className="text-left p-3">状态</th>
                    <th className="text-left p-3 rounded-r-lg w-[220px]">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                      <td className="p-3 text-white break-all max-w-[320px]">{h.url}</td>
                      <td className="p-3 text-gray-400">{h.date} {h.time}</td>
                      <td className="p-3"><span className="text-white font-bold text-[18px]">{h.total}</span></td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {h.sub.map(s => (
                            <div key={s.label} className="group relative">
                              <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                                style={{ background: `conic-gradient(${s.color} 0 ${s.v}%, rgba(255,255,255,0.06) ${s.v}% 100%)` }}>
                                <div className="w-[30px] h-[30px] rounded-full bg-[#0a0d18] flex items-center justify-center">{s.v}</div>
                              </div>
                              <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 rounded bg-black/80 text-[10px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                                {s.label}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/10 border border-emerald-400/30 text-emerald-300">
                          <CheckCircle2 size={11} /> {h.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5">
                          <IconBtn Icon={<ExternalLink size={12} />} label="查看" />
                          <IconBtn Icon={<Share2 size={12} />} label="分享" />
                          <IconBtn Icon={<Download size={12} />} label="PDF" />
                          <IconBtn Icon={<Trash2 size={12} />} label="删除" danger />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DimCard({Icon, iconBg, title, en, desc}:{
  Icon: React.ReactNode; iconBg: string; title: string; en: string; desc: string;
}) {
  return (
    <motion.div
      whileHover={{ y: -2 }}
      className="rounded-2xl bg-white/[0.03] border border-white/[0.08] p-6 hover:border-violet-400/40 hover:bg-white/[0.05] transition-all">
      <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center`}>{Icon}</div>
      <div className="mt-5 text-[18px] font-bold text-white">{title}</div>
      <div className="mt-1 text-[11px] tracking-[0.15em] text-gray-500">{en}</div>
      <div className="mt-3 text-[13px] text-gray-500 leading-relaxed">{desc}</div>
    </motion.div>
  );
}

function IconBtn({ Icon, label, danger }:{ Icon: React.ReactNode; label: string; danger?: boolean }) {
  return (
    <button className={`px-2 py-1 rounded text-[11.5px] border transition-all flex items-center gap-1 ${
      danger ? "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-rose-300 hover:border-rose-400/40"
             : "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-violet-300 hover:border-violet-400/40"
    }`}>{Icon} {label}</button>
  );
}

/** 内嵌 SVG 二维码（21x21 定位码+随机图案，视觉风格模仿真二维码） */
function QRCodeSVG() {
  // 21x21 图案（固定种子，保证渲染稳定）
  const size = 21;
  const pattern: number[][] = [];
  const seed = [0.73,0.11,0.55,0.37,0.82,0.24,0.61,0.08,0.47,0.92,0.19,0.68,0.43,0.88,0.32,0.77,0.21,0.59,0.95,0.14,0.71];
  for (let y=0;y<size;y++) {
    const row: number[] = [];
    for (let x=0;x<size;x++) row.push((seed[(x*3 + y*7 + x*y) % seed.length] + Math.sin(x*0.7+y*0.9))%1 > 0.55 ? 1 : 0);
    pattern.push(row);
  }
  // 三个定位角
  const finder = (ox:number, oy:number) => {
    for (let y=0;y<7;y++) for (let x=0;x<7;x++) {
      const edge = (x===0||x===6||y===0||y===6) ? 1 : 0;
      const center = (x>=2&&x<=4&&y>=2&&y<=4) ? 1 : 0;
      pattern[oy+y][ox+x] = edge || center ? 1 : 0;
    }
  };
  finder(0,0); finder(size-7,0); finder(0,size-7);
  // 小型对齐码（右下角附近）
  for (let y=0;y<5;y++) for (let x=0;x<5;x++) {
    const edge=(x===0||x===4||y===0||y===4)?1:0;
    const center=(x===2&&y===2)?1:0;
    pattern[13+y][13+x] = edge || center ? 1 : 0;
  }
  const cell = 100 / size;
  const rects = [];
  for (let y=0;y<size;y++) for (let x=0;x<size;x++) if (pattern[y][x]) {
    rects.push(<rect key={`${x}-${y}`} x={x*cell} y={y*cell} width={cell} height={cell} fill="#111" rx="0.5" />);
  }
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      {rects}
    </svg>
  );
}
